import { useState, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Alert, Spinner, Tabs } from './shared';

const RESULT_TABS = [
    { id: 'json', label: 'JSON' },
    { id: 'content', label: 'Content' },
];

export default function AdminCrawler() {
    const [url, setUrl] = useState('');
    const [textOnly, setTextOnly] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [activeTab, setActiveTab] = useState('json');

    const handleExtract = useCallback(async () => {
        const trimmed = url.trim();
        if (!trimmed) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await adminApi.crawlerExtract(trimmed, textOnly);
            setResult(res);
            if (!res.success) setError(res.error || 'Extract failed');
        } catch (err) {
            setError(err.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    }, [url, textOnly]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loading) handleExtract();
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-lg font-semibold text-gray-900">Crawler Test</h1>
                <p className="text-sm text-gray-500 mt-0.5">Extract content from any URL using crawler API</p>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <div className="flex items-center gap-3 mb-6">
                <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="https://example.com"
                    className="flex-1 h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer whitespace-nowrap select-none">
                    <input
                        type="checkbox"
                        checked={textOnly}
                        onChange={(e) => setTextOnly(e.target.checked)}
                        className="accent-blue-500"
                    />
                    Text only
                </label>
                <Button variant="primary" size="sm" onClick={handleExtract} disabled={loading || !url.trim()}>
                    {loading ? 'Extracting...' : 'Extract'}
                </Button>
            </div>

            {loading && <Spinner size="lg" text="Extracting content..." center />}

            {result && !loading && (
                <div>
                    <div className="flex items-center gap-3 mb-3">
                        <Tabs tabs={RESULT_TABS} activeTab={activeTab} onChange={setActiveTab} />
                        {result.duration_ms > 0 && (
                            <span className="text-xs text-gray-400 ml-auto">{result.duration_ms}ms</span>
                        )}
                    </div>

                    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                        {activeTab === 'json' && (
                            <pre className="p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap break-all overflow-auto max-h-[70vh]">
                                {JSON.stringify(result.raw_result || result, null, 2)}
                            </pre>
                        )}
                        {activeTab === 'content' && (
                            <div className="p-4 overflow-auto max-h-[70vh]">
                                {result.text_content ? (
                                    <pre className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                        {result.text_content}
                                    </pre>
                                ) : (
                                    <p className="text-sm text-gray-400 italic text-center py-8">No text content extracted</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
